/**
 * PTEL Visor - Map Constants
 * Constantes centralizadas para el módulo de mapas
 *
 * @module components/map/constants
 * @version 1.0.0
 *
 * Re-exporta configuraciones del servicio de tiles y define
 * constantes específicas del visor cartográfico.
 */

// =============================================================================
// RE-EXPORTS DEL SERVICIO DE TILES
// =============================================================================

export {
  // Configuraciones de proveedores
  IGN_PNOA_CONFIG,
  IGN_BASE_CONFIG,
  IGN_MTN_CONFIG,
  OSM_CONFIG,
  TILE_CONFIGS,
  PROVIDERS_BY_PRIORITY,
  // Tipos
  type TileProviderId,
  type TileConfig,
  type ActiveTileConfig,
  type HealthCheckResult,
  type ProviderStatus,
  // Utilidades
  isOfficialSpanishProvider,
  getCombinedAttribution,
  isZoomSupported,
} from '../../services/tiles/TileProvider';

// =============================================================================
// RE-EXPORTS DE LEAFLET-CRS
// =============================================================================

// CRS_EPSG25830: construir bajo demanda en el componente de mapa que lo necesite.
// NO exportar desde este módulo compartido — arrastra Leaflet + proj4leaflet
// al chunk constants y provoca crash en contextos sin DOM.
// Usar: import { getCRS } from '@/lib/leaflet-crs-map' solo en componentes de mapa.

export {
  EPSG_25830_DEF,
  WGS84_DEF,
  // Límites geográficos
  ANDALUCIA_BOUNDS_UTM,
  ANDALUCIA_BOUNDS_WGS84,
  ANDALUCIA_CENTER_WGS84,
  ANDALUCIA_CENTER_UTM,
  // Funciones de transformación
  toUTM30,
  toWGS84,
  pointToUTM30,
  pointToWGS84,
  // Validación
  isValidAndaluciaCoord,
  isValidAndaluciaWGS84,
  isValidUTM30Coord,
  // Utilidades
  distanceUTM,
  azimuthUTM,
  cardinalLabel,
  formatDisplacement,
  CARDINAL_ARROWS,
  formatDragFeedback,
  formatUTM,
  parseUTMString,
  // Puntos de verificación
  VERIFICATION_POINTS,
} from '../../lib/leaflet-crs';

export type { CardinalDirection } from '../../lib/leaflet-crs';

// =============================================================================
// CONSTANTES DEL VISOR
// =============================================================================

/** Centro por defecto del mapa: Andalucía */
export const DEFAULT_MAP_CENTER = {
  lat: 37.5,
  lng: -4.5,
} as const;

/** Centro en UTM30 */
export const DEFAULT_MAP_CENTER_UTM = {
  x: 370000,
  y: 4150000,
} as const;

/** Zoom por defecto para vista regional */
export const DEFAULT_ZOOM = 8;

/** Zoom para vista municipal */
export const MUNICIPAL_ZOOM = 12;

/** Zoom para vista de detalle (edificio) */
export const DETAIL_ZOOM = 16;

/** Rango de zoom permitido */
export const ZOOM_RANGE = {
  min: 5,
  max: 19,
} as const;

/** Proveedor de tiles preferido por defecto */
// eslint-disable-next-line @typescript-eslint/consistent-type-imports -- import() inline evita importar todo TileProvider en este módulo constante
export const DEFAULT_TILE_PROVIDER: import('../../services/tiles/TileProvider').TileProviderId = 'ign-base';

/** Timeout para carga de tiles (ms) */
export const TILE_LOAD_TIMEOUT = 5000;

/** Umbral de errores antes de fallback */
export const ERROR_THRESHOLD = 3;

// =============================================================================
// CONFIGURACIÓN DE MARCADORES
// =============================================================================

/** Tamaños de marcador por nivel de zoom */
export const MARKER_SIZES = {
  small: { width: 20, height: 20 },
  medium: { width: 28, height: 28 },
  large: { width: 36, height: 36 },
} as const;

/** Tamaño de marcador según zoom */
export function getMarkerSizeForZoom(zoom: number): keyof typeof MARKER_SIZES {
  if (zoom < 10) return 'small';
  if (zoom < 14) return 'medium';
  return 'large';
}

// =============================================================================
// CONFIGURACIÓN DE CLUSTERING
// =============================================================================

/** Configuración de clustering por defecto */
export const CLUSTER_CONFIG = {
  /** Radio máximo de cluster en píxeles */
  maxClusterRadius: 50,
  /** Zoom máximo para clustering */
  disableClusteringAtZoom: 16,
  /** Mostrar área de cobertura al hover */
  showCoverageOnHover: true,
  /** Animar división de clusters */
  animate: true,
  /** Spiderfy clusters pequeños */
  spiderfyOnMaxZoom: true,
} as const;

// =============================================================================
// COLORES DEL MAPA (PTEL Design System)
// =============================================================================

/** Colores para estados de coordenadas */
export const SCORE_COLORS = {
  /** Score 80-100: Válido */
  valid: '#A3E635',     // lime-400
  /** Score 50-79: Advertencia */
  warning: '#FACC15',   // yellow-400
  /** Score 20-49: Error */
  error: '#F97316',     // orange-500
  /** Score 0-19: Crítico */
  critical: '#EF4444',  // red-500
} as const;

/** Obtiene color según score */
export function getColorForScore(score: number): string {
  if (score >= 80) return SCORE_COLORS.valid;
  if (score >= 50) return SCORE_COLORS.warning;
  if (score >= 20) return SCORE_COLORS.error;
  return SCORE_COLORS.critical;
}

// =============================================================================
// TIPOS ADICIONALES
// =============================================================================

/** Punto geográfico WGS84 */
export interface WGS84Point {
  lat: number;
  lng: number;
}

/** Punto UTM30 */
export interface UTM30Point {
  x: number;
  y: number;
}

/** Bounds del mapa */
export interface MapBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}
